import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { fetchSubscription } from '../utils/fetchSubscription';
import { doc, serverTimestamp, setDoc, collection, onSnapshot, addDoc, deleteField } from 'firebase/firestore';
import { db } from '../firebase';

export default function useLimits() {
  const { user } = useAuth();
  const [limits, setLimits] = useState({});
  const [plan, setPlan] = useState('Free');

  useEffect(() => {
    const getLimits = async () => {
      if (!user?.uid) {
        setLimits({});
        return;
      }

      const { limits, subscription } = await fetchSubscription(user.uid);
      // If subscription missing we can't proceed
      if (!subscription) return;

      let resolvedLimits = limits;
      if (!resolvedLimits) {
        // If no limits, just set empty or handle elsewhere
        resolvedLimits = {};
      }

      setPlan(subscription.plan || 'Free');

      setLimits(resolvedLimits);
    };

    getLimits();
  }, [user]);

  // New: Listen for subscription document changes in real-time and refresh limits when changed.
  useEffect(() => {
    if (!user?.uid) return;
    const collRef = collection(db, 'users', user.uid, 'Subscription');
    const unsub = onSnapshot(collRef, async () => {
      try {
        const { limits: newLimits, subscription: newSubscription } = await fetchSubscription(user.uid);
        if (newSubscription) {
          setPlan(newSubscription.plan || 'Free');
        }

        // If there is a pending_activation and it's due, apply it now (automatic activation similar to daily reset)
        try {
          const pendingTs = newSubscription?.pending_activation;
          if (pendingTs) {
            const pendingDate = (typeof pendingTs.toDate === 'function') ? pendingTs.toDate() : new Date(pendingTs);
            if (new Date() >= pendingDate) {
              // apply pending plan
              const targetPlan = newSubscription.pending_plan || newSubscription.plan || 'Free';
              const targetCycle = newSubscription.pending_billingCycle ?? newSubscription.billingCycle ?? '';
              const subId = newSubscription.id;
              const subRef = doc(db, 'users', user.uid, 'Subscription', subId);
              // update subscription: set active plan and remove pending fields
              await setDoc(subRef, {
                plan: targetPlan,
                billingCycle: targetCycle,
                pending_plan: deleteField(),
                pending_billingCycle: deleteField(),
                pending_activation: deleteField(),
                updated_at: serverTimestamp()
              }, { merge: true });

              // write History entry
              try {
                const historyCol = collection(db, 'users', user.uid, 'Subscription', newSubscription.id, 'History');
                await addDoc(historyCol, {
                  event: 'applied_pending_activation',
                  to_plan: targetPlan,
                  billingCycle: targetCycle,
                  applied_on: serverTimestamp()
                });
              } catch (hErr) {
                console.warn('Failed to write activation history', hErr);
              }

              // reflect applied change in local state
              setPlan(targetPlan);
            }
          }
        } catch (applyErr) {
          console.warn('Error while checking/applying pending_activation', applyErr);
        }

        if (newLimits) {
          setLimits(newLimits);
        }
      } catch (err) {
        console.warn('Failed to refresh subscription on snapshot', err);
      }
    });

    return () => unsub();
  }, [user]);

  // Removed decrementLimit; backend triggers handle decrements
  return { ...limits, plan };
}
